import { memo, useCallback, type FC } from "react";
import { Modal } from "antd";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import MiniSpinner from "src/components/reusable/Spinner/MiniSpinner";
import { PREVIEW_MODAL_WIDTH, TEMPLATE_EDITOR_KEY } from "../TemplateEditorConstants";

const PREVIEW_KEY = `${TEMPLATE_EDITOR_KEY}.preview`;
const IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";

interface TemplatePreviewModalProps {
  templateId: string;
  html: string;
  isApplying: boolean;
  onClose: () => void;
  onUse: (_templateId: string) => void;
}

const TemplatePreviewModal: FC<TemplatePreviewModalProps> = ({
  templateId,
  html,
  isApplying,
  onClose,
  onUse,
}) => {
  const { t } = useTranslation();
  const handleUse = useCallback(() => onUse(templateId), [onUse, templateId]);
  const isLoading = !html;

  return (
    <Modal
      open
      centered
      footer={null}
      width={PREVIEW_MODAL_WIDTH}
      onCancel={onClose}
      closeIcon={<X size={18} aria-label={t(`${PREVIEW_KEY}.close`)} />}
      className="template-preview-modal campaign-flow__popup"
    >
      <div className="template-preview-modal__frame">
        {isLoading ? (
          <MiniSpinner />
        ) : (
          <iframe
            title={t(`${PREVIEW_KEY}.frameTitle`)}
            srcDoc={html}
            sandbox={IFRAME_SANDBOX}
            className="template-preview-modal__iframe"
          />
        )}
      </div>

      <div className="template-preview-modal__footer">
        <button
          type="button"
          className="button button--primary campaign-flow__action"
          onClick={handleUse}
          disabled={isLoading || isApplying}
        >
          {isApplying
            ? t(`${TEMPLATE_EDITOR_KEY}.card.applying`)
            : t(`${TEMPLATE_EDITOR_KEY}.card.useTemplate`)}
        </button>
      </div>
    </Modal>
  );
};

export default memo(TemplatePreviewModal);
